import { useMemoryCleanup } from "@/shared/hooks/useMemoryCleanup";
import { ComponentType, lazy, LazyExoticComponent } from "react";

interface LazyComponentOptions {
  fallback?: ComponentType;
  chunkName?: string;
  retryAttempts?: number;
}

/**
 * Enhanced lazy loading utility with retry mechanism and memory cleanup
 */
export const createLazyComponent = <T extends ComponentType<unknown>>(
  importFunction: () => Promise<{ default: T }>,
  options: LazyComponentOptions = {},
): LazyExoticComponent<T> => {
  const { retryAttempts = 3 } = options;

  const lazyImport = () => {
    let attempts = 0;

    const loadComponent = async (): Promise<{ default: T }> => {
      try {
        return await importFunction();
      } catch (error) {
        attempts++;

        // If it's a chunk loading error and we have retries left
        if (attempts < retryAttempts && error instanceof Error) {
          console.warn(
            `Failed to load chunk (attempt ${attempts}/${retryAttempts}):`,
            error.message,
          );

          // Wait a bit before retrying with exponential backoff
          await new Promise((resolve) =>
            setTimeout(resolve, Math.pow(2, attempts) * 1000),
          );

          return loadComponent();
        }

        console.error(
          "Failed to load component after all retry attempts:",
          error,
        );
        throw error;
      }
    };

    return loadComponent();
  };

  return lazy(lazyImport);
};

/**
 * Preload a lazy component
 */
export const preloadComponent = (importFunction: () => Promise<unknown>) => {
  return importFunction();
};

/**
 * Hook for managing lazy component preloading
 */
export const useLazyPreload = () => {
  const { isMounted } = useMemoryCleanup();

  const preload = (importFunction: () => Promise<unknown>) => {
    if (isMounted()) {
      return preloadComponent(importFunction);
    }
  };

  return { preload };
};
